import { FC, useCallback, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { RecordingState } from '../../appointments/recording/RecordingState';
import { INote } from '../generic-notes/INote';

const CARD_HEIGHT = 100; // approximate height of feedback card

const useStyles = createUseStyles({
    container: {
        padding: '12px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
    },
    prompt: {
        fontSize: 14,
        color: 'var(--ion-color-medium)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    thumbButton: {
        background: 'none',
        border: 'none',
        fontSize: 20,
        cursor: 'pointer',
        padding: 4,
        borderRadius: 4,
        '&:hover': {
            backgroundColor: 'var(--ion-color-light)',
        },
    },
    selectedThumb: {
        background: 'var(--ion-color-light)',
        borderRadius: 4,
    },
    unselectedThumb: {
        opacity: 0.4,
    },
    feedbackCard: {
        background: 'var(--ion-background-color, #fff)',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 260,
    },
    feedbackCardAbove: {
        background: 'var(--ion-background-color, #fff)',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 260,
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: 8,
    },
    feedbackInput: {
        fontSize: 14,
        padding: '6px 10px',
        border: '1px solid var(--ion-color-light-shade)',
        borderRadius: 6,
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        '&:focus': {
            borderColor: 'var(--ion-color-primary)',
        },
    },
    feedbackButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 4,
    },
    submitButton: {
        background: 'none',
        border: 'none',
        fontSize: 14,
        cursor: 'pointer',
        color: 'var(--ion-color-primary)',
        padding: '4px 8px',
        fontWeight: 500,
    },
    dismissButton: {
        background: 'none',
        border: 'none',
        fontSize: 14,
        cursor: 'pointer',
        color: 'var(--ion-color-medium)',
        padding: '4px 8px',
        fontWeight: 500,
    },
});

export const SummaryFeedback: FC<{ note: INote }> = ({ note }) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);
    const recordingState = useInstance(RecordingState);
    const promptRef = useRef<HTMLDivElement>(null);

    const persistedRating = note.otherAttributes?.recording?.summaryRating;

    const [optimisticRating, setOptimisticRating] = useState<'positive' | 'negative' | undefined>(persistedRating);
    const [feedbackText, setFeedbackText] = useState('');
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [showAbove, setShowAbove] = useState(false);

    // Sync optimistic state when the note prop changes (e.g. navigating back to the note)
    if (persistedRating !== undefined && optimisticRating === undefined) {
        setOptimisticRating(persistedRating);
    }

    const checkPosition = useCallback(() => {
        if (!promptRef.current) {
            return false;
        }
        const rect = promptRef.current.getBoundingClientRect();
        return rect.bottom + CARD_HEIGHT > window.innerHeight;
    }, []);

    const summary = note.otherAttributes?.recording?.summary;
    const hasRateableSummary = !!summary && !summary.isUnavailable;

    if (!hasRateableSummary) {
        return null;
    }

    const saveRating = (feedbackRating: 'positive' | 'negative') => {
        mixpanel.track('Summary Rated', () => ({
            noteId: note.noteId,
            summaryPrompt: note.otherAttributes?.recording?.summaryPrompt || 'appointment',
            rating: feedbackRating,
        }));
        recordingState.saveSummaryRating(note.noteId, feedbackRating);
    };

    const onThumbsUp = () => {
        if (optimisticRating === 'positive') {
            return;
        }
        setOptimisticRating('positive');
        setShowFeedbackForm(false);
        saveRating('positive');
    };

    const onThumbsDown = () => {
        if (optimisticRating === 'negative') {
            return;
        }
        setOptimisticRating('negative');
        saveRating('negative');
        setFeedbackText('');
        setShowAbove(checkPosition());
        setShowFeedbackForm(true);
    };

    const onSubmitFeedback = () => {
        if (!feedbackText.trim()) {
            return;
        }
        setShowFeedbackForm(false);
        mixpanel.track('Summary Feedback Submitted', () => ({
            noteId: note.noteId,
            summaryPrompt: note.otherAttributes?.recording?.summaryPrompt || 'appointment',
            feedback: feedbackText,
        }));
    };

    const onDismissFeedback = () => {
        setShowFeedbackForm(false);
    };

    const rating = optimisticRating;

    const thumbClass = (thumb: 'positive' | 'negative') => {
        if (!rating) {
            return classes.thumbButton;
        }
        return `${classes.thumbButton} ${rating === thumb ? classes.selectedThumb : classes.unselectedThumb}`;
    };

    return (
        <div className={classes.container} data-id="summary-feedback">
            <div className={classes.prompt} ref={promptRef}>
                <span>{rating ? (rating === 'positive' ? 'Thanks!' : 'Thanks for the feedback') : 'Was this summary helpful?'}</span>
                <button className={thumbClass('positive')} onClick={onThumbsUp} data-id="thumbs-up" data-selected={rating === 'positive' ? 'true' : undefined} aria-label="Thumbs up">
                    {'\uD83D\uDC4D'}
                </button>
                <button className={thumbClass('negative')} onClick={onThumbsDown} data-id="thumbs-down" data-selected={rating === 'negative' ? 'true' : undefined} aria-label="Thumbs down">
                    {'\uD83D\uDC4E'}
                </button>
            </div>

            {showFeedbackForm && (
                <div className={showAbove ? classes.feedbackCardAbove : classes.feedbackCard} data-id="feedback-form">
                    <input
                        className={classes.feedbackInput}
                        type="text"
                        placeholder="What was off?"
                        value={feedbackText}
                        onChange={e => setFeedbackText(e.target.value)}
                        data-id="feedback-input"
                        autoFocus
                    />
                    <div className={classes.feedbackButtons}>
                        <button className={classes.dismissButton} onClick={onDismissFeedback} data-id="feedback-dismiss">
                            Dismiss
                        </button>
                        <button className={classes.submitButton} onClick={onSubmitFeedback} data-id="feedback-submit" disabled={!feedbackText.trim()}>
                            Submit
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
